import { Alert, Box, Button } from '@mui/material';
import { useSnackbar } from 'notistack';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { usePayerAssignments } from '../hooks/usePayerAssignments';
import { useSubscriptionCheckout } from '../hooks/useSubscriptionCheckout';
import { getProduct } from '../subscriptions/catalog';
import { normalizeEntitlementsMap, ownsProduct } from '../subscriptions/entitlements';
import {
  clearPendingCheckout,
  isIosNativeApp,
  readPendingCheckout,
  subscribeNativeAppResumed,
} from '../utils/nativeCheckoutBridge';

const CHECKOUT_SYNC_EVENT = 'smsp:checkout-synced';
const BANNER_DELAY_MS = 60_000;

const NativeCheckoutReturnHandler: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { assignments, refetch } = usePayerAssignments();
  const { confirmCheckout } = useSubscriptionCheckout();
  const [showRefreshBanner, setShowRefreshBanner] = useState(false);
  const [hasPendingCheckout, setHasPendingCheckout] = useState(
    () => !!readPendingCheckout(),
  );
  const syncInFlight = useRef(false);

  const trySyncPendingCheckout = useCallback(async () => {
    if (!isIosNativeApp()) {
      return;
    }
    const pending = readPendingCheckout();
    if (!pending || syncInFlight.current) {
      return;
    }

    syncInFlight.current = true;
    try {
      const confirmResult = await confirmCheckout(pending.sessionId);
      if (confirmResult) {
        clearPendingCheckout();
        setHasPendingCheckout(false);
        setShowRefreshBanner(false);
        await refetch();
        enqueueSnackbar(
          confirmResult.alreadyFulfilled
            ? `${confirmResult.productName} is already active.`
            : `Purchase complete — ${confirmResult.productName} is now unlocked.`,
          { variant: 'success' },
        );
        window.dispatchEvent(new CustomEvent(CHECKOUT_SYNC_EVENT, { detail: confirmResult }));
        return;
      }

      const refreshedAssignments = await refetch();
      const assignment = refreshedAssignments.find((item) =>
        pending.assignmentId !== undefined ? item.id === pending.assignmentId : true,
      );
      const entitlements = normalizeEntitlementsMap(assignment?.entitlements);
      if (assignment && ownsProduct(entitlements, pending.productId)) {
        clearPendingCheckout();
        setHasPendingCheckout(false);
        setShowRefreshBanner(false);
        const productName = getProduct(pending.productId)?.name ?? pending.productId;
        enqueueSnackbar(`Purchase complete — ${productName} is now unlocked.`, {
          variant: 'success',
        });
        window.dispatchEvent(
          new CustomEvent(CHECKOUT_SYNC_EVENT, {
            detail: { productId: pending.productId, assignmentId: assignment.id },
          }),
        );
      }
    } finally {
      syncInFlight.current = false;
    }
  }, [confirmCheckout, enqueueSnackbar, refetch]);

  useEffect(() => {
    if (!isIosNativeApp()) {
      return undefined;
    }

    const pending = readPendingCheckout();
    if (!pending) {
      setHasPendingCheckout(false);
      return undefined;
    }
    setHasPendingCheckout(true);

    void trySyncPendingCheckout();

    const bannerTimer = window.setTimeout(() => {
      if (readPendingCheckout()) {
        setShowRefreshBanner(true);
      }
    }, BANNER_DELAY_MS);

    const unsubscribe = subscribeNativeAppResumed(() => {
      void trySyncPendingCheckout();
    });

    return () => {
      window.clearTimeout(bannerTimer);
      unsubscribe();
    };
  }, [trySyncPendingCheckout]);

  if (!isIosNativeApp() || !showRefreshBanner || !hasPendingCheckout) {
    return null;
  }

  return (
    <Box
      sx={{
        position: 'fixed',
        left: 16,
        right: 16,
        bottom: 88,
        zIndex: (theme) => theme.zIndex.snackbar,
      }}
    >
      <Alert
        severity="info"
        action={
          <Button color="inherit" size="small" onClick={() => void trySyncPendingCheckout()}>
            Refresh purchases
          </Button>
        }
      >
        Finished your purchase? Tap refresh to unlock your content.
      </Alert>
    </Box>
  );
};

export default NativeCheckoutReturnHandler;
